import { Link } from "react-router-dom"
import { podsPath, webDevelopmentPath, workDetailPath, workPath } from "~/common/paths"
import { servicePresentations } from "~/content/service-presentations"
import type { TestimonialId } from "~/content/testimonials"
import { HeadlineText } from "~/marketing/headline-text"
import { SourceContactEndcap } from "~/marketing/public-page"
import { TestimonialQuote } from "~/marketing/testimonial-quote"
import { WorkCover } from "~/marketing/work-cover"

const serviceTestimonials = {
  "ai-strategy": "nick-kroetz",
  "web-development": "soraya-darabi",
  "design": "bre-pettis",
  "product": "yosh-miller",
  "sprint-zero": "adam-seifer",
} as const satisfies Record<keyof typeof servicePresentations, TestimonialId>

export const ServicePage = ({ page }: { page: keyof typeof servicePresentations }) => {
  const content = servicePresentations[page]
  const next =
    page === "sprint-zero"
      ? { to: webDevelopmentPath({}), label: "Explore development →" }
      : page === "ai-strategy"
        ? { to: workPath({}), label: "Explore our work →" }
        : { to: podsPath({}), label: "See how Pods work →" }

  return (
    <div className="service-page">
      <section className="service-section service-hero">
        <div className="service-container service-equal-grid">
          <div className="service-promise">
            <p className="service-eyebrow">{content.label}</p>
            <h1 className="marketing-page-title marketing-split-title">
              <span className="headline-beat">{content.lead}</span>{" "}
              <span className="headline-beat">
                <HeadlineText text={content.headline} accent={content.headlineAccent} />
              </span>
            </h1>
            <p className="service-intro">{content.body}</p>
            <a className="service-cta" href="#service-contact">
              Talk about your project
            </a>
          </div>
          <img
            className="service-mascot"
            src={`/images/services/${content.slug}.png`}
            width={620}
            height={560}
            alt=""
            fetchPriority="high"
          />
        </div>
      </section>
      <section className="service-section service-brown">
        <div className="service-container">
          <div className="service-detail-grid">
            <h2>{content.outcome}</h2>
            <p className="service-intro">{content.outcomeBody}</p>
          </div>
          <div className="service-outcomes">
            {content.outputs.map(([heading, body]) => (
              <div key={heading}>
                <h3>{heading}</h3>
                <p>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="service-section">
        <div className="service-container">
          <div className="service-detail-grid">
            <div className="service-introduction">
              <h2>{content.process}</h2>
              <p className="service-intro">{content.processBody}</p>
            </div>
            <ol className="service-steps">
              {content.steps.map(([heading, body]) => (
                <li key={heading}>
                  <div>
                    <h3>{heading}</h3>
                    <p>{body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div className="service-workflow">
            <p>{content.artifactTitle}</p>
            <ol>
              {content.artifact.map((stage) => (
                <li key={stage}>
                  <span className="service-number" aria-hidden="true">
                    →
                  </span>
                  <strong>{stage}</strong>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>
      <section className="service-section service-brown">
        <div className="service-container service-equal-grid service-proof">
          <WorkCover id={content.proofId} />
          <div className="service-introduction">
            <p className="service-eyebrow">{content.proofName}</p>
            <h2>{content.proof}</h2>
            <p>{content.proofBody}</p>
            <Link to={workDetailPath({ slug: content.proofId })}>{content.proofLink}</Link>
          </div>
        </div>
      </section>
      <TestimonialQuote id={serviceTestimonials[page]} />
      <section className="service-section">
        <div className="service-container">
          <div className="service-detail-grid">
            <div className="service-introduction">
              <h2>{content.start}</h2>
              <p className="service-intro">{content.startBody}</p>
            </div>
            <div className="service-entries">
              {content.entry.map(([heading, body]) => (
                <div key={heading}>
                  <h3>{heading}</h3>
                  <p>{body}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="service-bridge">
            <p>{content.bridge}</p>
            <Link to={next.to}>{next.label}</Link>
          </div>
        </div>
      </section>
      <div id="service-contact" className="service-contact">
        <SourceContactEndcap
          copy={{ heading: content.close, body: content.closeBody }}
          showNewsletter={false}
        />
      </div>
    </div>
  )
}
